import React, { useEffect, useRef, useState } from "react";
import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import {
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
} from "../../appconfig/Settings";
import { Column } from "primereact/column";
import { SubContractorListColumn } from "../../appconfig/DatatableSetting";
import { deleteData, getAllDetails, postAllData } from "../../service/ProductService";
import { ADD_SUB_CONTRACTOR_DETAIL, DELETE_SUB_CONTRACTOR, GET_SUB_CONTRACTOR_LIST, BULK_ADD_SUB_CONTRACTOR_PAYMENT } from "../../config/ApiConstant";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { subContractorDetailForm } from "../../config/Validate";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import SubContractorTab from "./SubContractorTab";
import { commonFieldTemplate } from "../../appconfig/TemplateFieldSetting";

const SubContractor = () => {
  document.title = "SubContractor | Nexawage";

  const { id } = useParams();
  const clientParamI = id ? atob(id) : null;

  const location: any = useLocation();
  const clientParamId = location?.state?.clientId;

  const toast = useRef<any>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  // bulk upload dialog state
  const [csvDialog, setCsvDialog] = useState<boolean>(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvDragOver, setCsvDragOver] = useState<boolean>(false);
  const [csvError, setCsvError] = useState<string>("");
  const [csvRowErrors, setCsvRowErrors] = useState<{ row: number; errors: string[] }[]>([]);
  const [csvSendMail, setCsvSendMail] = useState<0 | 1>(0);
  const navigate = useNavigate();

  const rawUserData = localStorage.getItem("user_data");
  let userData: any = null;
  userData = rawUserData ? JSON.parse(rawUserData) : null;
  const userRole = userData?.roles?.role_name || "";

  // filter
  const emptyFilters = {
    global: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    email: {
      operator: FilterOperator.OR,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    phone: {
      operator: FilterOperator.OR,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
  };
  const [filters, setFilters] = useState(emptyFilters);

  // client id based on login
  let clientId: any = null;
  if (userRole === "User") {
    clientId = clientParamId;
  } else {
    clientId = userData?.id;
  }

  //   filter
  const [globalFilter, setGlobalFilter] = useState<any>(null);
  const [globalFieldFilter, setGlobalFieldFilter] = useState<any>(null);

  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [subContractorList, setSubContractorList] = useState<any>([]);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  // selected Row id
  const [selectedRowId, setSelectedRowId] = useState<any>(null);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false);

  // isedit true false
  const [isEdit, setIsEdit] = useState<boolean>(false);

  // error state
  const [errors, setErrors] = useState<any>({});

  //   payee add and edit state
  const [subContractorData, setSubContractorData] = useState<any>({});

  //   add edit payee dailod
  const [addEditSubContractorDialog, setAddEditSubContractorDialog] = useState<boolean>(false);

  //   useEffect to get subContractorList
  useEffect(() => {
    getSubContractorList();
  }, []);

  // onchange
  const onInputChange = (e: any, name: string) => {
    let val = e.target.value ?? e.value;

    setSubContractorData({ ...subContractorData, [name]: val });
    setErrors({ ...errors, [name]: "" });
  };

  // hide add & edit payee modal
  const hideAddEditSubContractorDialog = () => {
    setAddEditSubContractorDialog(false);
    setSubmitLoading(false);
  };

  //   get payee list api call
  const getSubContractorList = async () => {
    try {
      setPageLoad(false);

      const response = await getAllDetails(`${GET_SUB_CONTRACTOR_LIST}/${clientId}`);
      setSubContractorList(response);
      setPageLoad(true);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  // add and edit payee modal
  const addEditDialog = (rowData: any = null) => {
    if (rowData) {
      setSubContractorData({ ...rowData });
      setIsEdit(true);
    } else {
      setSubContractorData({});
      setIsEdit(false);
    }
    setErrors({});
    setAddEditSubContractorDialog(true);
  };

  // submit payee form
  const submitSubContractorForm = async () => {
    const { isError, errors } = subContractorDetailForm(subContractorData);
    setErrors(errors);
    if (isError) return;

    setSubmitLoading(true);
    try {
      const payload = {
        client_id: clientId,
        name: subContractorData?.name,
        email: subContractorData?.email,
        phone: subContractorData?.phone,
        address: subContractorData?.address,
        sin_no: subContractorData?.sin_no,
        gst_no: subContractorData?.gst_no,
        ...(isEdit && { id: subContractorData?.id }),
      };

      const response = await postAllData(ADD_SUB_CONTRACTOR_DETAIL, payload);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        hideAddEditSubContractorDialog();
        getSubContractorList();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  // open / close upload csv dialog
  const openCsvDialog = () => {
    setCsvFile(null);
    setCsvError("");
    setCsvRowErrors([]);
    setCsvDialog(true);
  };

  const hideCsvDialog = () => {
    setCsvDialog(false);
    setCsvFile(null);
    setCsvError("");
    setCsvRowErrors([]);
    setSubmitLoading(false);
  };

  // validate and keep selected file
  const pickCsvFile = (file?: File) => {
    if (!file) return;

    const name = file.name.toLowerCase();
    if (!name.endsWith(".csv") && !name.endsWith(".xlsx")) {
      setCsvFile(null);
      setCsvError("Please select a .csv or .xlsx file");
      return;
    }
    setCsvFile(file);
    setCsvError("");
    setCsvRowErrors([]);
  };

  const onCsvFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // reset so the same file can be selected again
    e.target.value = "";
    pickCsvFile(file);
  };

  // bulk upload api call, sendMail = 1 emails each sub contractor their PDF, 0 only saves
  const submitCsv = async (sendMail: 0 | 1) => {
    if (!csvFile) {
      setCsvError("Please select a file to upload");
      return;
    }
    try {
      setSubmitLoading(true);
      setCsvSendMail(sendMail);
      setCsvRowErrors([]);
      const formData = new FormData();
      formData.append("file", csvFile);
      formData.append("client_id", clientId);
      formData.append("send_mail", String(sendMail));
      const response = await postAllData(BULK_ADD_SUB_CONTRACTOR_PAYMENT, formData);
      if (response) {
        toast.current?.show({ severity: "success", summary: "Success", detail: response.message });
        hideCsvDialog();
        getSubContractorList();
      }
    } catch (error: any) {
      const rowErrors = error?.response?.data?.errors;
      if (Array.isArray(rowErrors) && rowErrors.length > 0) {
        setCsvRowErrors(rowErrors);
        setCsvError(error?.response?.data?.message || "Please fix the errors in the file and upload again");
      } else {
        toast.current?.show({ severity: "error", summary: "Error", detail: error?.response?.data?.message });
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  // clear all fields
  const ClearAll = () => {
    setFilters({ ...emptyFilters });
    setGlobalFilter(null);
    setGlobalFieldFilter(null);
  };

  //   left toolbar template
  const leftToolbarTemplate = () => (
    <>
      <span className="block mr-2 p-input-icon-left">
        <i className="pi pi-search" />
        <InputText type="search" value={globalFilter || ""} onChange={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
      </span>
    </>
  );

  //   right toolbar template
  const rightToolbarTemplate = () => (
    <>
      <div style={{ display: "flex", alignItems: "end" }}>
        <Button icon="pi pi-filter-slash" label="Clear" onClick={ClearAll} className="mr-3  p-button-outlined common-p-outline-button" />
        <Button
          icon="pi pi-upload"
          label="Upload CSV"
          onClick={openCsvDialog}
          className="mr-3 p-button-outlined common-p-outline-button"
        />
      </div>
      <Button label="Add New Sub Contractor" icon="pi pi-plus" className="p-button-primary" onClick={() => addEditDialog()} />
    </>
  );

  // delete confirmation modal
  const confirmDelete = (deleteID: any) => {
    setSelectedRowId(deleteID);
    setDeleteDialog(true);
  };

  // delete employee api call
  const deleteSubContractor = async () => {
    try {
      setSubmitLoading(true);
      const response = await deleteData(DELETE_SUB_CONTRACTOR, selectedRowId);
      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Deleted",
          detail: response.message,
        });
        hideDeleteDialog();
        getSubContractorList();
        setSubmitLoading(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message,
      });
      setSubmitLoading(false);
    }
  };

  // hide delete modal
  const hideDeleteDialog = () => {
    setDeleteDialog(false);
    setSelectedRowId(null);
  };

  // action body template
  const actionBodyTemplate = (rowData: any) => (
    <div className="actions">
      <Button
        icon="pi pi-pencil"
        tooltip="Edit"
        className="common-edit-btn "
        tooltipOptions={{ position: "bottom" }}
        onClick={() => addEditDialog(rowData)}
      />

      <Button
        icon="pi pi-trash"
        tooltipOptions={{ position: "bottom" }}
        className="ml-2 p-button-danger common-delete-btn"
        tooltip="Delete"
        onClick={() => confirmDelete(rowData.id)}
      />
    </div>
  );

  // create invoice template
  const createInvoiceTemplate = (rowData: any) => {
    return (
      <div className="name">
        <Button
          tooltip={"Proceed To Payment"}
          tooltipOptions={{ position: "bottom" }}
          className="ml-2 common-primary-button"
          icon="pi pi-plus"
          label=""
          onClick={() =>
            navigate(`/payment/${btoa(rowData?.id)}`, {
              state: { clientId: clientId },
            })
          }
        />
      </div>
    );
  };

  return (
    <>
      <AppBreadcrumb currentMenu={"Sub Contractor"} pageName={"Detail"} pageTitle={"Sub Contractor"} goToPage={userRole === "User"} />
      <div className="grid crud-demo">
        <div className="col-12">
          <Card className="bg-white crud-demo lg:p-2">
            <SubContractorTab index={0} id={clientId} />
            <Toast ref={toast} />
            <Toolbar className="gap-3" left={leftToolbarTemplate} right={rightToolbarTemplate} />

            {pageLoad ? (
              <DataTable
                value={subContractorList}
                showGridlines
                dataKey="id"
                stripedRows
                globalFilter={globalFilter}
                rows={defaultRowOptions}
                paginator={subContractorList?.length > 0}
                rowsPerPageOptions={defaultPageRowOptions}
                className="datatable-responsive p-datatble-sm"
                paginatorTemplate={paginatorLinks}
                currentPageReportTemplate={showingEntries + " Sub Contractor List"}
                emptyMessage="No Sub Contractor Found"
                filters={globalFieldFilter}
                onFilter={(e) => setGlobalFieldFilter(e.filters)}
                removableSort
                responsiveLayout={"scroll"}
              >
                {SubContractorListColumn.map((col, i) => {
                  if (col.field === "action") {
                    return <Column key={col.field} field={col.field} header={col.header} body={actionBodyTemplate} />;
                  } else if (col.field === "payment") {
                    return <Column key={col.field} field={col.field} header={col.header} body={createInvoiceTemplate} />;
                  } else if (col.field === "sin_no") {
                    return <Column key={col.field} field={col.field} header={col.header} body={commonFieldTemplate} />;
                  } else if (col.field === "gst_no") {
                    return <Column key={col.field} field={col.field} header={col.header} body={commonFieldTemplate} />;
                  } else {
                    return <Column key={col.field} field={col.field} header={col.header} />;
                  }
                })}
              </DataTable>
            ) : (
              <DataTable value={Skeletonitems}>
                {SubContractorListColumn.map((col, i) => (
                  <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                ))}
              </DataTable>
            )}
          </Card>
        </div>
      </div>

      {/* Add/Edit payee Dialog */}
      <Dialog
        visible={addEditSubContractorDialog}
        className="mx-3 lg:mx-0"
        style={{ width: "800px" }}
        header={isEdit ? "Edit Sub Contractor Detail" : "Add Sub Contractor"}
        draggable={false}
        modal
        footer={
          <div className="flex pt-2 align-items-center justify-content-end">
            <Button label="Cancel" icon="pi pi-times" className="mr-2 p-button-secondary" onClick={hideAddEditSubContractorDialog} />
            <Button label="Submit" icon="pi pi-check" onClick={submitSubContractorForm} className="common-primary-button" loading={submitLoading} />
          </div>
        }
        onHide={hideAddEditSubContractorDialog}
      >
        <div className="grid p-fluid formgrid">
          {/* Name */}
          <div className="field col-12 md:col-6">
            <label htmlFor="name">Name *</label>
            <div className="input-effect-div">
              <InputText
                value={subContractorData.name || ""}
                name="name"
                autoComplete="off"
                placeholder="Enter Name"
                onChange={(e) => onInputChange(e, "name")}
                className={errors["name"] && "p-invalid"}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["name"]}</small>
          </div>

          {/* Email */}
          <div className="field col-12 md:col-6">
            <label htmlFor="email">Email *</label>
            <div className="input-effect-div">
              <InputText
                value={subContractorData.email || ""}
                name="email"
                autoComplete="off"
                placeholder="Enter Email"
                onChange={(e) => onInputChange(e, "email")}
                className={errors["email"] && "p-invalid"}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["email"]}</small>
          </div>

          {/* Phone */}
          <div className="field col-12 md:col-6">
            <label htmlFor="phone">Phone *</label>
            <div className="flex-1 input-effect-div p-inputgroup">
              <span className="font-bold p-inputgroup-addon">+1</span>
              <InputText
                value={subContractorData.phone || ""}
                name="phone"
                autoComplete="off"
                placeholder="Enter Phone Number"
                onChange={(e) => onInputChange(e, "phone")}
                className={errors["phone"] && "p-invalid"}
                keyfilter="pint"
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["phone"]}</small>
          </div>

          {/* SIN number */}
          <div className="field col-12 md:col-6">
            <label htmlFor="number">SIN Number/Business No </label>
            <div className="input-effect-div">
              <InputText
                value={subContractorData.sin_no || ""}
                name="sin_no"
                autoComplete="off"
                placeholder="Enter SIN No OR Business No"
                onChange={(e) => onInputChange(e, "sin_no")}
                className={errors["sin_no"] && "p-invalid"}
                // keyfilter="pint"
                maxLength={15}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["sin_no"]}</small>
          </div>

          {/* Gst No */}
          <div className="field col-12 md:col-6">
            <label htmlFor="number">GST Number </label>
            <div className="input-effect-div">
              <InputText
                value={subContractorData.gst_no || ""}
                name="gst_no"
                autoComplete="off"
                placeholder="Enter Gst Number"
                onChange={(e) => onInputChange(e, "gst_no")}
                className={errors["gst_no"] && "p-invalid"}
                maxLength={15}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["gst_no"]}</small>
          </div>

          {/* Address */}
          <div className="field col-12 md:col-6">
            <label htmlFor="address">Address *</label>
            <div className="input-effect-div">
              <InputTextarea
                rows={3}
                value={subContractorData.address || ""}
                name="address"
                autoComplete="off"
                placeholder="Enter Address"
                onChange={(e) => onInputChange(e, "address")}
                className={errors["address"] && "p-invalid"}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["address"]}</small>
          </div>
        </div>
      </Dialog>

      {/* Upload CSV Dialog */}
      <Dialog
        visible={csvDialog}
        style={{ width: "560px" }}
        draggable={false}
        className="mx-3 lg:mx-0"
        modal
        header={
          <div className="flex align-items-center gap-3">
            <span
              className="flex align-items-center justify-content-center border-round"
              style={{ width: "2.5rem", height: "2.5rem", background: "#eef2ff", color: "#4f46e5" }}
            >
              <i className="pi pi-cloud-upload text-xl" />
            </span>
            <div>
              <div className="font-bold text-xl">Bulk Upload Payments</div>
              <div className="text-sm font-normal" style={{ color: "#6c757d" }}>
                Add multiple sub contractor payments from one file
              </div>
            </div>
          </div>
        }
        footer={
          <div className="flex pt-2 align-items-center justify-content-between">
            <Button label="Cancel" className="p-button-text p-button-secondary" disabled={submitLoading} onClick={hideCsvDialog} />
            <div className="flex gap-2">
              <Button
                label="Submit & Send Email"
                icon="pi pi-send"
                className="p-button-outlined common-p-outline-button"
                disabled={submitLoading || !csvFile}
                loading={submitLoading && csvSendMail === 1}
                onClick={() => submitCsv(1)}
              />
              <Button
                label="Submit"
                icon="pi pi-check"
                className="common-primary-button"
                disabled={submitLoading || !csvFile}
                loading={submitLoading && csvSendMail === 0}
                onClick={() => submitCsv(0)}
              />
            </div>
          </div>
        }
        onHide={hideCsvDialog}
      >
        <input ref={csvInputRef} type="file" accept=".csv,.xlsx" style={{ display: "none" }} onChange={onCsvFileSelect} />

        {/* drop zone / selected file */}
        {csvFile ? (
          <div className="flex align-items-center justify-content-between gap-3 p-3 border-round" style={{ border: "1px solid #b7e4c7", background: "#f0fbf4" }}>
            <div className="flex align-items-center gap-3" style={{ minWidth: 0 }}>
              <i className="pi pi-file-excel text-2xl" style={{ color: "#2d9d5b" }} />
              <div style={{ minWidth: 0 }}>
                <div className="font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis">{csvFile.name}</div>
                <div className="text-xs" style={{ color: "#6c757d" }}>
                  {(csvFile.size / 1024).toFixed(1)} KB
                </div>
              </div>
            </div>
            <Button
              icon="pi pi-times"
              className="p-button-text p-button-rounded p-button-secondary"
              tooltip="Remove file"
              tooltipOptions={{ position: "left" }}
              disabled={submitLoading}
              onClick={() => {
                setCsvFile(null);
                setCsvRowErrors([]);
              }}
            />
          </div>
        ) : (
          <div
            className="flex flex-column align-items-center justify-content-center text-center p-4 border-round cursor-pointer"
            style={{ border: `2px dashed ${csvDragOver ? "#4f46e5" : "#c5cad3"}`, background: csvDragOver ? "#eef2ff" : "#fafbfc" }}
            onClick={() => csvInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setCsvDragOver(true);
            }}
            onDragLeave={() => setCsvDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setCsvDragOver(false);
              pickCsvFile(e.dataTransfer.files?.[0]);
            }}
          >
            <i className="pi pi-upload text-3xl mb-2" style={{ color: "#8a93a2" }} />
            <div className="font-medium">
              Drag &amp; drop your file here, or <span style={{ color: "#4f46e5" }}>browse</span>
            </div>
            <div className="text-xs mt-1" style={{ color: "#6c757d" }}>
              Supports .csv and .xlsx
            </div>
          </div>
        )}
        {csvError && (
          <small className="p-invalid-txt block mt-2">
            <i className="pi pi-exclamation-circle mr-1" />
            {csvError}
          </small>
        )}

        {/* row level errors returned by the api */}
        {csvRowErrors.length > 0 && (
          <div className="mt-2 p-2 border-round text-sm" style={{ background: "#fff5f5", border: "1px solid #f5c2c7", maxHeight: "180px", overflowY: "auto" }}>
            {csvRowErrors.map((item) => (
              <div key={item.row} className="mb-2">
                <b>Row {item.row}</b>
                <ul className="my-1 pl-4">
                  {item.errors.map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {/* guidelines */}
        <div className="mt-4 p-3 border-round text-sm" style={{ background: "#f6f7f9", color: "#495057" }}>
          <div className="flex align-items-center justify-content-between mb-2">
            <span className="font-bold">Before you upload</span>
            <a href="/subcontractor_payment_bulk_upload_sample.csv" download className="inline-flex align-items-center gap-1 font-medium">
              <i className="pi pi-download" /> Sample file
            </a>
          </div>
          <ul className="m-0 pl-4" style={{ lineHeight: 1.7 }}>
            <li>Fields marked * are required. Keep the header row unchanged.</li>
            <li>Dates use DD/MM/YYYY. Payment Method: E-Transfer or Cheque. GST Included: Yes or No.</li>
            <li>One row per service. Repeat Name, Email, Payment Method and GST Included on every row.</li>
            <li>
              <b>Submit</b> saves only; <b>Submit &amp; Send Email</b> also emails each sub contractor their PDF.
            </li>
          </ul>
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        visible={deleteDialog}
        header="Delete Sub Contractor"
        draggable={false}
        className="mx-3 lg:mx-0"
        modal
        footer={
          <div className="flex pt-2 align-items-center justify-content-end">
            <Button label="Cancel" icon="pi pi-times" className="mr-2 p-button-secondary" onClick={hideDeleteDialog} />
            <Button label="Delete" icon="pi pi-trash" className="p-button-danger" onClick={deleteSubContractor} loading={submitLoading} />
          </div>
        }
        onHide={hideDeleteDialog}
      >
        <div className="flex align-items-center justify-content-start">
          <i className="mr-3 pi pi-exclamation-triangle delete-triangle" style={{ fontSize: "2rem" }} />
          <span className="delete-dialog-note">Are you sure you want to delete this Sub Contractor ?</span>
        </div>
      </Dialog>
    </>
  );
};

export default SubContractor;
